import { formatAlternatePrice, formatPrice } from 'app/helpers/format-helper';
import { ApiSubmission } from 'app/types/reviews';

type NullableAmount = string | null;

type SubmissionPriceDisplay = {
  base: NullableAmount;
  bonus: NullableAmount;
  total: NullableAmount;
};

const normalizeAmount = (value?: NullableAmount): NullableAmount => (value == null || value === '' ? null : value);

export const getSubmissionPriceDisplay = (submission: ApiSubmission): SubmissionPriceDisplay => {
  const exchangeCurrency = submission.exchange_currency ?? '';
  const exchangeCurrencyPrice = normalizeAmount(submission.exchange_currency_price);
  const isPriceInExchangeCurrency =
    exchangeCurrency !== '' && exchangeCurrencyPrice !== null && exchangeCurrencyPrice !== '0';
  const totalPrice = normalizeAmount(submission.price);
  const basePrice = normalizeAmount(submission.base_price);
  const localBonusAmount = normalizeAmount(submission.local_bonus_amount);

  // base_price and local_bonus_amount are provided in local currency when exchange currency is enabled
  if (isPriceInExchangeCurrency) {
    return {
      base: basePrice ? formatAlternatePrice(exchangeCurrency, basePrice) : null,
      bonus: localBonusAmount ? formatAlternatePrice(exchangeCurrency, localBonusAmount) : null,
      total: formatAlternatePrice(exchangeCurrency, exchangeCurrencyPrice ?? '0'),
    };
  }

  return {
    base: basePrice ? formatPrice(basePrice) : null,
    bonus: localBonusAmount ? formatPrice(localBonusAmount) : null,
    total: formatPrice(totalPrice ?? '0'),
  };
};

// https://github.com/udacity/reviews-api/blob/master/config/initializers/i18n.rb#L1-L11
export const submissionLanguages = [
  {
    code: 'zh-cn',
    i18nKey: 'submissionLanguages.chineseSimplified',
  },
  {
    code: 'en-us',
    i18nKey: 'submissionLanguages.english',
  },
  {
    code: 'pt-br',
    i18nKey: 'submissionLanguages.portugueseBrazil',
  },
  {
    code: 'ar',
    i18nKey: 'submissionLanguages.arabic',
  },
  {
    code: 'es',
    i18nKey: 'submissionLanguages.spanish',
  },
  {
    code: 'fr-ca',
    i18nKey: 'submissionLanguages.frenchCanada',
  },
  {
    code: 'fr',
    i18nKey: 'submissionLanguages.french',
  },
  {
    code: 'ko',
    i18nKey: 'submissionLanguages.korean',
  },
  {
    code: 'de',
    i18nKey: 'submissionLanguages.german',
  },
  {
    code: 'tr',
    i18nKey: 'submissionLanguages.turkish',
  },
  {
    code: 'el',
    i18nKey: 'submissionLanguages.greek',
  },
  {
    code: 'ja',
    i18nKey: 'submissionLanguages.japanese',
  },
  {
    code: 'pl',
    i18nKey: 'submissionLanguages.polish',
  },
] as const;

const languageMap: Map<string, string> = new Map(submissionLanguages.map((lang) => [lang.code, lang.i18nKey]));

export function getSubmissionLanguageI18nKey(code: string): string {
  return languageMap.get(code) ?? code;
}
